import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LinearProgress,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useFetch } from "utils/api/useFetch";
import useOrganization from "utils/useOrganization";
import EmptyState from "./EmptyState";
import ProviderForm from "./Form";

import { AIProvider, AISettings } from "@dashboard-v3/api";

export default function ExternalAI() {
  const { t } = useTranslation("storage");
  const classes = useStyles();
  const { revalidateOrganization } = useOrganization();
  const [selectedProviderId, setSelectedProviderId] = useState<string>(null);
  const providers = useFetch<AIProvider[]>("/intelligence/ai/providers");
  const settings = useFetch<AISettings | null>("/intelligence/ai");

  const isLoading = providers.isLoading || settings.isLoading;
  const hasError = Boolean(providers.error || settings.error);
  const isReady = !isLoading && !hasError;
  const hasSelection = Boolean(settings.data || selectedProviderId);
  const showEmptyState = isReady && !hasSelection;
  const showForm = isReady && hasSelection;

  async function handleSaved() {
    await settings.mutate();
    revalidateOrganization();
  }

  function handleDisconnected() {
    setSelectedProviderId(null);
    settings.mutate(null, { revalidate: false });
    revalidateOrganization();
  }

  return (
    <>
      <div className={classes.intro}>
        <Typography variant="subtitle2">{t("externalAI.title")}</Typography>
        <Typography variant="caption" color="textSecondary">
          {t("externalAI.description")}
        </Typography>
      </div>
      {isLoading && (
        <Paper className={classes.loading}>
          <LinearProgress />
        </Paper>
      )}
      {!isLoading && hasError && (
        <Alert severity="error">{t("externalAI.error.fetch")}</Alert>
      )}
      {showEmptyState && (
        <EmptyState
          providers={providers.data}
          onSelect={setSelectedProviderId}
        />
      )}
      {showForm && (
        <ProviderForm
          providers={providers.data}
          settings={settings.data}
          initialProviderId={selectedProviderId}
          onSaved={handleSaved}
          onDisconnected={handleDisconnected}
          onCancel={() => setSelectedProviderId(null)}
        />
      )}
    </>
  );
}

const useStyles = makeStyles({
  intro: {
    display: "flex",
    flexDirection: "column",
    marginBottom: 16,
  },
  loading: { padding: "12px 16px" },
});
